/**
 * Контракты типоразмеров конвейеров (E04.4, ФТ-3.2.1, ФТ-3.2.2).
 *
 * Типоразмер — каталожная запись модели (УКЛС-800) со значениями атрибутов
 * уровня «константа конвейера». Пересчёт формульных констант — после E05.
 */

import { z } from "zod";
import {
  attributeInputTypeSchema,
  attributeValueTypeSchema,
  formulaNameSchema,
} from "./attributes";
import { apiSuccessSchema } from "./envelope";
import { publishStatusSchema } from "./catalog";

const codeSchema = z.string().trim().min(1).max(128);
const sortOrderSchema = z.number().int().min(0).max(100_000);

/** Значение константы типоразмера. */
export const unitAttributeValueSchema = z.object({
  attributeId: z.string().uuid(),
  attributeName: z.string().min(1),
  formulaName: formulaNameSchema.nullable(),
  valueType: attributeValueTypeSchema,
  value: z.string().min(1).max(512),
  isCalculated: z.boolean(),
});

export type UnitAttributeValue = z.infer<typeof unitAttributeValueSchema>;

/** Поле схемы модели со scope=unit — что заполнять в карточке типоразмера. */
export const unitAttributeFieldSchema = z.object({
  attributeId: z.string().uuid(),
  attributeName: z.string().min(1),
  formulaName: formulaNameSchema.nullable(),
  valueType: attributeValueTypeSchema,
  inputType: attributeInputTypeSchema,
  isRequired: z.boolean(),
  defaultValue: z.string().nullable(),
  sortOrder: sortOrderSchema,
  groupName: z.string().nullable(),
  scope: z.literal("unit"),
});

export type UnitAttributeField = z.infer<typeof unitAttributeFieldSchema>;

/** Типоразмер конвейера. */
export const conveyorUnitSchema = z.object({
  id: z.string().uuid(),
  modelId: z.string().uuid(),
  modelName: z.string().min(1),
  code: codeSchema,
  status: publishStatusSchema,
  attributeValues: z.array(unitAttributeValueSchema),
  /** Сколько версий комплектовочной ведомости привязано — видно, что затронет удаление. */
  bomVersionCount: z.number().int().nonnegative(),
});

export type ConveyorUnit = z.infer<typeof conveyorUnitSchema>;

const attributeValueInputSchema = z.object({
  attributeId: z.string().uuid(),
  value: z.string().trim().min(1).max(512),
});

/** Тело `POST /api/v1/admin/models/{modelId}/units`. */
export const createConveyorUnitRequestSchema = z.object({
  code: codeSchema,
  status: publishStatusSchema.default("draft"),
  attributeValues: z.array(attributeValueInputSchema).default([]),
});

export type CreateConveyorUnitRequest = z.infer<typeof createConveyorUnitRequestSchema>;

/** Тело `PATCH /api/v1/admin/models/{modelId}/units/{id}`. */
export const updateConveyorUnitRequestSchema = z
  .object({
    code: codeSchema.optional(),
    status: publishStatusSchema.optional(),
    attributeValues: z.array(attributeValueInputSchema).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateConveyorUnitRequest = z.infer<typeof updateConveyorUnitRequestSchema>;

export const conveyorUnitListResponseSchema = apiSuccessSchema(z.array(conveyorUnitSchema));
export const conveyorUnitResponseSchema = apiSuccessSchema(conveyorUnitSchema);
export const unitAttributeFieldListResponseSchema = apiSuccessSchema(
  z.array(unitAttributeFieldSchema),
);
